import { inject, Injectable } from "@angular/core";
import {
    type CardsByCardIdMap,
    type IBaseCard,
    type JobsBillingCard,
    type TaxprepT1Card,
    CardCategoryDisplayPreference,
    DisplayCardBasedOn,
    IBaseT1Card,
    isiFirmTaxDashboard,
    isJobsBilling,
    isJobsBillingDashboard,
    isTaxprepDashboard,
    isTaxprepT1Card,
} from "@app/views-interfaces";
import { ICardFieldLabelsModel } from "@app/views-interfaces/card-field-labels-model.interface";
import { CalcCardFieldLabelsEvent, OpenCardEvent } from "@app/views-interfaces/card.interface";
import { ICategories } from "@app/views-interfaces/categories.interface";
import { IReturnStatusHistory } from "@app/views-interfaces/return-status-history.interface";
import { HubConnection } from "@microsoft/signalr";
import { CardsService } from "@services/index";
import { firstValueFrom, Subject, Subscription } from "rxjs";
import { SignalRService } from "../../real-time-notifications/signalr-service/signalr.service";
import { isCardGrouped } from "../../views-interfaces/card-utils";
import { IDashboardModel } from "../../views-interfaces/dashboard-model.interface";
import { AuthService } from "../auth";
import { CardService, ICardServiceResponse } from "../card-service/card.service";
import { CryptoService, EncryptedCard } from "../crypto-service/crypto.service";
import { DashboardsService } from "../dashboards-service/dashboards.service";
import { HelpersService } from "../helpers-service/helpers.service";
import { CardDisplayModel, CardDisplayModelEvent, CardEvent, CardEventType, CardModelEvent } from "../interfaces";
import { MappingOfStatusesService } from "../mapping-of-statuses-service/mapping-of-statuses.service";
import { StepsService } from "../steps-service/steps.service";
import { TaxReturnStatusesService } from "../tax-return-statuses-service/tax-return-statuses.service";
import { CategoriesService } from "./../categories-service/categories.service";

@Injectable({
    providedIn: "root",
})
export class CardDisplayService {
    private model: CardDisplayModel[];
    private jobsAndBillingStatusOrderById: Map<string, number> = new Map();

    private allCards: any[] = [];
    private allCardsById: CardsByCardIdMap = new Map();
    public calculatedCategoriesPerCard = {};
    private modelPromise;
    private allCardsPromise;
    private signalRDisconnectTimestamp = 0;
    private signalRInitiated = false;

    public calcCardFieldLabelsSubject = new Subject<CalcCardFieldLabelsEvent>();
    public openCardSubject = new Subject<OpenCardEvent>();
    public modelChangedSubject = new Subject<CardDisplayModelEvent>();
    public cardChangedSubject = new Subject<CardEvent>();

    openCardModalEvent$ = this.openCardSubject.asObservable();
    calcCardFieldLabelsEvent$ = this.calcCardFieldLabelsSubject.asObservable();
    modelChangedEvent$ = this.modelChangedSubject.asObservable();
    cardChangedEvent$ = this.cardChangedSubject.asObservable();

    hideCardModalArrows = false;

    private readonly signalRConnectedSubscription: Subscription;
    private readonly sessionChangedSubscription: Subscription;
    private readonly dashboardChangeSubscription: Subscription;
    private readonly cardChangedSubscription: Subscription;
    private readonly currentDashboarChangedSubscription: Subscription;

    private currentDashboard: IDashboardModel;

    private readonly authService = inject(AuthService);
    private readonly cardService = inject(CardService);
    private readonly dashboardsService = inject(DashboardsService);
    private readonly cryptoService = inject(CryptoService);
    private readonly stepService = inject(StepsService);
    private readonly cardsService = inject(CardsService);
    private readonly signalRService = inject(SignalRService);
    private readonly taxReturnStatusService = inject(TaxReturnStatusesService);
    private readonly mappingOfStatusesService = inject(MappingOfStatusesService);
    private readonly categoriesService = inject(CategoriesService);
    private readonly helpersService = inject(HelpersService);

    constructor() {
        this.dashboardChangeSubscription = this.dashboardsService.dashboardChangeEvent$.subscribe(() => {
            this.currentDashboard = null;
            this.calcCardFieldLabelsCache = {};
            this.reset();
        });

        this.currentDashboarChangedSubscription = this.dashboardsService.currentDashboardChanged$.subscribe((dashboardId) => {
            this.currentDashboard = this.dashboardsService.Dashboards.find((it) => it.Id === dashboardId);
        });

        this.sessionChangedSubscription = this.authService.sessionChangedReceivedEvent$.subscribe(() => {
            if (this.signalRDisconnectTimestamp > 0) {
                this.syncLocalCardsWithServer(this.allCardsById);
            }
        });
        this.signalRConnectedSubscription = this.signalRService.signalRConnectedEvent$.subscribe((connected) => {
            if (connected) {
                this.listenToSignalRMessages();
                if (this.signalRDisconnectTimestamp > 0) {
                    if (this.authService.loggedIn()) {
                        this.syncLocalCardsWithServer(this.allCardsById);
                    }
                }
            } else {
                this.stopListeningToSignalRMessages();
                // Skip cards reload if not loaded yet
                if (this.allCardsPromise) {
                    this.signalRDisconnectTimestamp = Date.now();
                }
            }
        });
    }

    destructor() {
        this.sessionChangedSubscription?.unsubscribe();
        this.signalRConnectedSubscription?.unsubscribe();
        this.dashboardChangeSubscription?.unsubscribe();
        this.cardChangedSubscription?.unsubscribe();
        this.signalRConnectedSubscription?.unsubscribe();
        this.currentDashboarChangedSubscription?.unsubscribe();
    }

    reset() {
        this.modelPromise = undefined;
        this.model = undefined;
        this.jobsAndBillingStatusOrderById.clear();
        this.allCardsPromise = undefined;
        this.allCards = [];
        this.allCardsById.clear();
        this.calculatedCategoriesPerCard = {};
        // Clear the shared cards signal synchronously to prevent another timing issue with J&B cards read as T1, and vice-versa, when
        // switching dashboards (DASH-21677). Anti-pattern to have a service directly modify another service's signal, but the two
        // services are already intermingled.
        this.cardsService.cards.set(new Map());
    }

    async getModel() {
        this.modelPromise ??= this.getModel_internal();
        await this.modelPromise;
        return this.model;
    }

    private async getModel_internal() {
        this.model = [];

        const cardsQuery = this.loadAllCards();
        const stepsQuery = this.stepService.getStepsList();
        const returnStatusesQuery = this.taxReturnStatusService.getTaxReturnStatusesList();

        const [cards, steps, returnStatuses] = await Promise.all([cardsQuery, stepsQuery, returnStatusesQuery]);

        this.jobsAndBillingStatusOrderById.clear();
        if (isJobsBillingDashboard(this.currentDashboard)) {
            returnStatuses.forEach((status, index) => {
                if (status.Id) {
                    this.jobsAndBillingStatusOrderById.set(status.Id, status.Order ?? index);
                }
            });
        }

        steps.sort((a, b) => a.Order - b.Order);

        for (const step of steps) {
            for (const status of step.Statuses) {
                this.model.push({
                    state: step.State,
                    stepId: step.Id,
                    stepName: step.Name,
                    statusId: status.TaxReturnStatusId,
                    roleId: status.RoleId,
                    cards: [],
                } as CardDisplayModel);
            }
        }

        for (const status of returnStatuses) {
            const statusInModel = this.model.find((m) => m.statusId === status.Id);
            if (!statusInModel) {
                this.model.push({
                    state: "unmapped",
                    statusId: status.Id,
                    cards: [],
                } as CardDisplayModel);
            }
        }

        if (cards && Array.isArray(cards) && cards.length) {
            for (const card of cards) {
                this.model.find((item) => item.statusId === this.getCardDisplayStatusId(card))?.cards.push(card);
            }
        }

        return this.model;
    }

    /**
     * Loads all cards either from indexedDB or server, and returns the cards as an array.
     * This will ensure the consistency of the local cache with differences from the server.
     */
    async loadAllCards() {
        this.allCardsPromise ??= this.loadAllCards_internal();
        await this.allCardsPromise;
        return this.allCards;
    }

    async loadAllCardsByIds() {
        this.allCardsPromise ??= this.loadAllCards_internal();
        await this.allCardsPromise;
        return this.allCardsById;
    }

    private async loadAllCards_internal() {
        const userId = this.authService.currentUser.sub;
        if (!this.currentDashboard) {
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        }
        const dashboardId = this.currentDashboard?.Id;

        const localCards = await this.loadAllCardsFromIndexedDB(userId, dashboardId);
        if (localCards.length == 0) {
            console.info("No cards found in cache.");
            this.allCards = await this.fetchAndStoreAllCardsFromServer(userId, dashboardId);
        } else if (localCards && localCards.length > 0) {
            // Some cards in cache : check for differences.
            const query = this.getSelectForDashboard(this.currentDashboard);
            const response = await firstValueFrom(this.cardService.getCardsFromServer("99999999", query));
            if (response.Code === 200) {
                // Download and put in cache new and updated cards
                const localCardsMap = localCards.reduce((acc, card) => {
                    acc[card.cardId] = card;
                    return acc;
                }, {});
                const cardIdsToFetch = response.Cards.filter(
                    (remote) => !localCardsMap[remote.cardId] || localCardsMap[remote.cardId].ts != remote._ts,
                ).map((remote) => remote.cardId);

                if (cardIdsToFetch.length > 1000) {
                    // Too much diff, faster to load them all again.
                    this.allCards = await this.fetchAndStoreAllCardsFromServer(userId, dashboardId);
                } else {
                    // In parallel, decrypt local cards
                    const decryptedPromise = localCards.map((encrypted) => this.cryptoService.decrypt(encrypted));
                    // Fetch all cards to update, 20 cards at a time, then update the local cache.
                    const requests: Promise<ICardServiceResponse>[] = [];
                    const limit = 20;

                    for (let i = 0; i < cardIdsToFetch.length; i += limit) {
                        const toFetch = cardIdsToFetch.slice(i, i + limit);
                        const query = toFetch.map((cardId) => `cardId eq '${encodeURIComponent(cardId)}'`).join(" or ");

                        requests.push(firstValueFrom(this.cardService.getCardsFromServer("99999999", `&$filter=(${query})`, "99999999")));
                    }

                    // While we wait for a response, remove deleted cards from the cache
                    const remoteCardsMap = response.Cards.reduce((acc, card) => {
                        acc[card.cardId] = card;
                        return acc;
                    }, {});
                    const removedCards = localCards.filter((local) => !remoteCardsMap[local.cardId]);
                    if (removedCards.length > 0) {
                        this.removeCardsFromIndexedDB(
                            userId,
                            dashboardId,
                            removedCards.map((card) => card.cardId),
                        );
                        for (const card of removedCards) {
                            delete localCardsMap[card.cardId];
                        }
                    }

                    // Wait and process all requested cards
                    const responses = await Promise.all(requests);
                    const updatedCards = responses.filter((r) => r.Code === 200).flatMap((r) => Object.values(r.Cards));
                    if (updatedCards.length > 0) {
                        this.storeCardsInIndexedDB(userId, dashboardId, updatedCards);
                    }
                    for (const card of updatedCards) {
                        localCardsMap[card.cardId] = card;
                    }
                    // Wait and process all decrypted cards
                    const decrypted = await Promise.all(decryptedPromise);
                    for (const card of decrypted) {
                        if (localCardsMap[card.cardId]?.ts === card._ts) {
                            localCardsMap[card.cardId] = card;
                        }
                    }
                    this.allCards = Object.values(localCardsMap);
                }
            }
        }

        for (const card of this.allCards) {
            this.allCardsById.set(card.cardId, card);
        }

        this.cardsService.cards.set(this.allCardsById);
    }

    /**
     * Fetches all cards from the server, and stores them to the indexedDB as a side effect.
     */
    async fetchAndStoreAllCardsFromServer(userId: string, dashboardId: string) {
        let result: IBaseCard[] = [];
        const response = await firstValueFrom(this.cardService.getCardsFromServer());
        if (response.Code === 200) {
            result = response.Cards;
            // No need to await on this before returning.
            this.storeCardsInIndexedDB(userId, dashboardId, result);
        }
        return result;
    }

    async syncLocalCardsWithServer(localCardsMap: CardsByCardIdMap) {
        const userId = this.authService.currentUser.sub;
        if (!this.currentDashboard) {
            this.currentDashboard = await this.dashboardsService.getCurrentDashboard();
        }
        const dashboardId = this.currentDashboard?.Id;
        const query = this.getSelectForDashboard(this.currentDashboard);
        const response = await firstValueFrom(this.cardService.getCardsFromServer("99999999", query));
        if (response.Code === 200) {
            // Download and put in cache new and updated cards
            const cardIdsToFetch = response.Cards.filter(
                (remote) => !localCardsMap.get(remote.cardId) || localCardsMap.get(remote.cardId)._ts != remote._ts,
            ).map((remote) => remote.cardId);

            if (cardIdsToFetch.length > 1000) {
                // Too much diff, faster to load them all again.
                await this.fetchAndStoreAllCardsFromServer(userId, dashboardId);
            } else {
                // Fetch all cards to update, 20 cards at a time, then update the local cache.
                const requests: Promise<ICardServiceResponse>[] = [];
                const limit = 20;

                for (let i = 0; i < cardIdsToFetch.length; i += limit) {
                    const toFetch = cardIdsToFetch.slice(i, i + limit);
                    const query = toFetch.map((cardId) => `cardId eq '${encodeURIComponent(cardId)}'`).join(" or ");

                    requests.push(firstValueFrom(this.cardService.getCardsFromServer("99999999", `&$filter=(${query})`, "99999999")));
                }

                // While we wait for a response, remove deleted cards from the cache
                const remoteCardsMap = response.Cards.reduce((acc, card) => {
                    acc[card.cardId] = card;
                    return acc;
                }, {});
                const removedCards: IBaseCard[] = Array.from(localCardsMap.values()).filter((local) => !remoteCardsMap[local.cardId]);
                if (removedCards.length > 0) {
                    this.removeCardsFromIndexedDB(
                        userId,
                        dashboardId,
                        removedCards.map((card) => card.cardId),
                    );
                    for (const card of removedCards) {
                        localCardsMap.delete(card.cardId);
                    }
                }

                // Wait and process all requested cards
                const responses = await Promise.all(requests);
                const updatedCards = responses.filter((r) => r.Code === 200).flatMap((r) => Object.values(r.Cards));
                if (updatedCards.length > 0) {
                    this.storeCardsInIndexedDB(userId, dashboardId, updatedCards);
                }
                for (const card of updatedCards) {
                    localCardsMap.set(card.cardId, card as TaxprepT1Card);
                }
            }
        }
    }

    /**
     * Remove cards in the indexedDB.
     */
    async removeCardsFromIndexedDB(userId: string, dashboardId: string, cardIds: string[]) {
        const db = await this.openCardDB(userId, dashboardId);
        const transaction = db.transaction("Cards", "readwrite");
        const store = transaction.objectStore("Cards");
        for (const cardId of cardIds) {
            store.delete(cardId);
        }
        transaction.oncomplete = function () {
            db.close();
        };
    }

    /**
     * Encrypts and store cards in the indexedDB.
     */
    async storeCardsInIndexedDB(userId: string, dashboardId: string, cards: IBaseCard[]) {
        const encryptedCards = await Promise.all(cards.map((card) => this.cryptoService.encrypt(card)));
        const db = await this.openCardDB(userId, dashboardId);
        const transaction = db.transaction("Cards", "readwrite");
        const store = transaction.objectStore("Cards");
        for (const card of encryptedCards) {
            store.put(card);
        }
        transaction.oncomplete = function () {
            db.close();
        };
    }

    /**
     * Loads all encrypted cards stored in the userId_dashboardId database.
     */
    async loadAllCardsFromIndexedDB(userId: string, dashboardId: string): Promise<EncryptedCard[]> {
        const db = await this.openCardDB(userId, dashboardId);
        const transaction = db.transaction("Cards", "readonly");
        const request = transaction.objectStore("Cards").getAll();
        return new Promise((resolve, reject) => {
            request.onsuccess = async (e: any) => {
                resolve(e.target.result);
            };
            request.onerror = (e) => {
                console.error(`Could not get all items from ${userId}_${dashboardId}`, e);
                reject(e);
            };
            transaction.oncomplete = function () {
                db.close();
            };
        });
    }

    /**
     * Opens this userId_dashboardId database, initializing it if it does not exists.
     * Returns an IDBDatabase ready to be used.
     */
    async openCardDB(userId: string, dashboardId: string): Promise<IDBDatabase> {
        return new Promise((resolve, reject) => {
            const request = indexedDB.open(`${userId}_${dashboardId}`);
            request.onupgradeneeded = (e: any) => {
                const db = e.target.result;
                db.createObjectStore("Cards", { keyPath: "cardId" });
            };
            request.onerror = (e) => {
                console.error("Could not open indexedDB", e);
                reject(e);
            };
            request.onsuccess = (e: any) => {
                resolve(e.target.result);
            };
        });
    }

    getSelectForDashboard(dashboard: IDashboardModel): string {
        let select = "";
        if (isiFirmTaxDashboard(dashboard)) {
            select = "&$select=cardId,documentId,contactInformation.iFirmContactId,_ts";
        } else if (isTaxprepDashboard(dashboard)) {
            select = "&$select=cardId,_ts";
        } else if (isJobsBillingDashboard(dashboard)) {
            select = "&$select=cardId,_ts";
        }
        return select;
    }

    storeCard(card: IBaseCard, storeTimestamp = true) {
        if (!card) {
            return false;
        }
        if (this.allCardsById.size > 0) {
            if (isTaxprepT1Card(card) && this.calculatedCategoriesPerCard[card.cardId]) {
                // Only revert to "Unassigned" if user didn't explicitly set to null
                // Explicit null means user selected "Unassigned", so don't overwrite it
                if (card.categoryId !== null && card.categoryId === this.calculatedCategoriesPerCard[card.cardId].Id) {
                    card.categoryId = "Unassigned";
                }
            }
        }
        const storedcard = this.getCardById(card.cardId);
        if (storedcard?._ts) {
            if (!storeTimestamp) {
                // Only prevent overwrite of timestamp if it has already been stored. Will be over written on loadCardById
                card._ts = storedcard._ts;
            }
        }
        this.commonStoreCard(card);
    }

    storeCardObservable(card: IBaseCard) {
        if (isTaxprepT1Card(card) && this.calculatedCategoriesPerCard[card.cardId]) {
            // Only revert to "Unassigned" if user didn't explicitly set to null
            // Explicit null means user selected "Unassigned", so don't overwrite it
            if (card.categoryId !== null && card.categoryId === this.calculatedCategoriesPerCard[card.cardId].Id) {
                card.categoryId = "Unassigned";
            }
        }
        this.commonStoreCard(card);
    }

    private commonStoreCard(card: IBaseCard) {
        const cardId = card?.cardId;
        if (!cardId) {
            return;
        }

        const existingCard = this.getCardById(cardId);
        const nextCard = {
            ...existingCard,
            ...card,
        };

        this.allCardsById.set(cardId, nextCard);

        const existingIdx = existingCard ? this.allCards.indexOf(existingCard) : -1;
        if (existingIdx > -1) {
            this.allCards[existingIdx] = nextCard;
        } else {
            this.allCards.push(nextCard);
        }

        this.cardsService.cards.update((cards) => {
            const updatedCards = new Map(cards);
            updatedCards.set(cardId, nextCard);
            return updatedCards;
        });
        this.storeCardsInIndexedDB(this.authService.currentUser.sub, this.currentDashboard?.Id, [card]);
    }

    getCardById(cardId: string): IBaseCard {
        return this.allCardsById.get(cardId) as unknown as IBaseCard;
    }

    removeCardById(cardId: string) {
        const modelEvent = {
            eventType: CardEventType.cardDeleted,
            oldModel: this.removeCardById_internal(cardId),
        } as CardDisplayModelEvent;
        if (modelEvent.oldModel?.card) {
            this.cardsService.cards.update((cards) => {
                const updatedCards = new Map(cards);
                updatedCards.delete(cardId);
                return updatedCards;
            });
            this.modelChangedSubject.next(modelEvent);

            const cardEvent = {
                eventType: CardEventType.cardDeleted,
                oldCard: modelEvent.oldModel?.card,
            } as CardEvent;
            this.cardChangedSubject.next(cardEvent);
        }
    }

    removeCardById_internal(cardId: string): CardModelEvent {
        const card = this.getCardById(cardId);
        let cardStatusModel: CardDisplayModel = null;
        if (card) {
            cardStatusModel = this.model.find((model) => model.cards.some((c) => c.cardId === cardId)) ?? null;
            if (cardStatusModel) {
                const cardModelIdx = cardStatusModel.cards.findIndex((c) => c.cardId === cardId);
                if (cardModelIdx > -1) {
                    cardStatusModel.cards.splice(cardModelIdx, 1);
                }
            }

            const idx = this.allCards.indexOf(card);
            if (idx > -1) {
                this.allCards.splice(idx, 1);
            }

            if (this.calculatedCategoriesPerCard[card.cardId]) {
                delete this.calculatedCategoriesPerCard[card.cardId];
            }
            this.allCardsById.delete(cardId);
        }
        return { model: cardStatusModel, card } as CardModelEvent;
    }

    addCard(card: IBaseCard, storeTimestamp = true) {
        const modelEvent = {
            eventType: CardEventType.cardAdded,
            newModel: this.addCard_Internal(card, storeTimestamp),
        } as CardDisplayModelEvent;
        this.modelChangedSubject.next(modelEvent);

        const cardEvent = {
            eventType: CardEventType.cardAdded,
            newCard: modelEvent.newModel?.card,
        } as CardEvent;
        this.cardChangedSubject.next(cardEvent);
    }

    addCard_Internal(card: IBaseCard, storeTimestamp = true): CardModelEvent {
        let cardStatusModel: CardDisplayModel = null;
        if (card) {
            this.storeCard(card, storeTimestamp);
            const cardStatusId = this.getCardDisplayStatusId(card as TaxprepT1Card);
            cardStatusModel = this.model?.find((m) => m.statusId === cardStatusId);
            if (cardStatusModel) {
                cardStatusModel.cards.push(card);
            }
        }
        return { model: cardStatusModel, card } as CardModelEvent;
    }

    updateCardById(cardId: string, card: IBaseCard, storeTimestamp = true) {
        const modelEvent = {
            eventType: CardEventType.cardUpdated,
            oldModel: this.removeCardById_internal(cardId),
            newModel: this.addCard_Internal(card, storeTimestamp),
        } as CardDisplayModelEvent;

        this.moveFamilyOnMemberUpdate(card);

        this.modelChangedSubject.next(modelEvent);

        const cardEvent = {
            eventType: CardEventType.cardUpdated,
            oldCard: modelEvent.oldModel?.card,
            newCard: modelEvent.newModel?.card,
        } as CardEvent;
        this.cardChangedSubject.next(cardEvent);

        this.calcCardFieldLabelsSubject.next({
            cardId,
        });
    }

    private moveFamilyOnMemberUpdate(updatedCard: IBaseCard) {
        if (!updatedCard || !this.model || !this.currentDashboard) return;
        if (this.currentDashboard.DisplayCardBasedOn !== DisplayCardBasedOn.LeastAdvanced) return;

        const taxCard = updatedCard as IBaseT1Card;
        if (!taxCard.return) return;
        if (!isCardGrouped(taxCard, this.currentDashboard)) return;

        const targetStatusId = this.getCardDisplayStatusId(taxCard);
        for (const member of taxCard.members ?? []) {
            if (member.cardId === updatedCard.cardId) continue;
            const memberCard = this.getCardById(member.cardId);
            if (memberCard) this.moveCardInModel(memberCard, targetStatusId);
        }
    }

    private moveCardInModel(card: IBaseCard, targetStatusId: string) {
        if (!this.model || !card) return;

        const currentBin = this.model.find((bin) => bin.cards.some((c) => c.cardId === card.cardId));
        if (currentBin?.statusId === targetStatusId) return;

        if (currentBin) {
            const idx = currentBin.cards.findIndex((c) => c.cardId === card.cardId);
            if (idx > -1) {
                currentBin.cards.splice(idx, 1);
            }
        }

        this.model.find((bin) => bin.statusId === targetStatusId)?.cards.push(card);
    }

    async getFullCard(cardId) {
        let card = this.getCardById(cardId);
        if (card === undefined) {
            const response = await firstValueFrom(this.cardService.getCardById(cardId));
            if (response.Code === 200) {
                card = response.Card as IBaseCard;
                this.storeCardObservable(card);
            }
        }
        return card;
    }

    listenToSignalRMessages() {
        if (this.signalRInitiated) {
            return false;
        }
        this.signalRInitiated = true;
        const connection: HubConnection = this.signalRService.connection;
        connection.on("card-updated", (cardId, cardData) => {
            console.info("card-updated", cardId, cardData);
            if (typeof cardData === "string") {
                cardData = JSON.parse(cardData);
            }
            if (cardData?.return) {
                // Guard against poisoned events whose payload belongs to a different card than the event
                // key. Storing such a payload under the key desyncs the cache and leads to saving one
                // member's data onto another's URL (404 + crash). See DASH-21397.
                if (cardData.cardId && cardId && cardData.cardId !== cardId) {
                    console.warn("Ignoring card-updated with mismatched id", { key: cardId, payloadCardId: cardData.cardId });
                    return;
                }
                this.updateCardById(cardId, cardData);
            }
        });

        connection.on("card-deleted", (cardId) => {
            console.info("card-deleted", cardId);
            this.removeCardById(cardId);
        });

        connection.on("card-added", (cardId, cardData) => {
            console.info("card-added", cardId, cardData);
            if (typeof cardData === "string") {
                cardData = JSON.parse(cardData);
            }
            if (cardData?.return) {
                this.addCard(cardData);
            }
        });

        connection.on("cards-added", () => {
            console.info("cards-added");
            this.reset();
            const modelEvent = {
                eventType: CardEventType.cardsAdded,
            } as CardDisplayModelEvent;
            this.modelChangedSubject.next(modelEvent);
            const cardEvent = {
                eventType: CardEventType.cardsAdded,
            } as CardEvent;
            this.cardChangedSubject.next(cardEvent);
        });

        connection.on("cards-deleted", () => {
            console.info("cards-deleted");
            this.reset();
            const modelEvent = {
                eventType: CardEventType.cardsDeleted,
            } as CardDisplayModelEvent;
            this.modelChangedSubject.next(modelEvent);
            const cardEvent = {
                eventType: CardEventType.cardsDeleted,
            } as CardEvent;
            this.cardChangedSubject.next(cardEvent);
        });
    }

    stopListeningToSignalRMessages(): boolean {
        if (!this.signalRInitiated) {
            return false;
        }
        this.signalRInitiated = false;
        const connection: HubConnection = this.signalRService.connection;
        if (connection) {
            connection.off("card-updated");
            connection.off("card-deleted");
            connection.off("card-added");
            connection.off("cards-added");
            connection.off("cards-deleted");
        }
    }

    /**
     * For a given card, returns the appropriate return status to use for display. Takes into account current dashboard settings
     * for DisplayGroupedCards, DisplayCardBasedOn and if the card is split.
     * @param card
     * @returns
     */
    getCardDisplayStatusId(card: IBaseCard): string {
        if (isJobsBilling(this.currentDashboard.ApplicationType)) {
            const jobCard = card as JobsBillingCard;
            return jobCard.job.statusId;
        } else {
            const taxCard = card as IBaseT1Card;
            if (isCardGrouped(taxCard, this.currentDashboard) && this.currentDashboard.DisplayCardBasedOn === DisplayCardBasedOn.LeastAdvanced) {
                // Find the return status for the least advanced step for all cards, starting with the current card.
                return taxCard.members
                    .map((member) => this.getCardById(member.cardId) as IBaseT1Card)
                    .filter((member) => member != undefined && isCardGrouped(member, this.currentDashboard)) // If the members are not all in cache, we just skip them for the moment so we don't crash
                    .reduce((leastAdvancedStatusId, memberCard: TaxprepT1Card) => {
                        const leastAdvancedStep = this.mappingOfStatusesService.getStepFromStatusId(leastAdvancedStatusId);
                        const memberStep = this.mappingOfStatusesService.getStepFromStatusId(memberCard.return.statusId);
                        if (memberStep && leastAdvancedStep && StepsService.compareSteps(memberStep, leastAdvancedStep) < 0) {
                            leastAdvancedStatusId = memberCard.return.statusId;
                        }
                        return leastAdvancedStatusId;
                    }, taxCard.return.statusId);
            } else {
                return taxCard.return.statusId;
            }
        }
    }

    calculateCategoryForCard(cardData: IBaseT1Card): ICategories | null {
        const getCategory = (card: any): ICategories | null => {
            // If overridden, use the explicit category
            if (card.categoryId && card.categoryId !== "Unassigned" && this.categoriesService.getCategory(card.categoryId)) {
                return this.categoriesService.getCategory(card.categoryId);
            }
            // Otherwise, calculate from tags
            return this.categoriesService.calculateCategory(this.currentDashboard, card.tags);
        };

        // If no members, just return main taxpayer category
        if (!cardData.members || cardData.members.length === 0) {
            return getCategory(cardData);
        }

        // If MostComplex, calculate for all members and return the most complex
        if (
            this.currentDashboard.DisplayGroupedCards &&
            this.currentDashboard.CardCategoryDisplayPreference === CardCategoryDisplayPreference.MostComplex &&
            !cardData.isSplit
        ) {
            let mostComplexCategory: ICategories | null = null;
            let mostComplexOrder: number | null = null;

            cardData.members.forEach((member) => {
                const cMember = this.getCardById(member.cardId);
                if (cMember) {
                    const category = getCategory(cMember);
                    if (category && (mostComplexOrder === null || category.Order < mostComplexOrder)) {
                        mostComplexCategory = category;
                        mostComplexOrder = category.Order;
                    }
                }
            });

            // Optionally, include main taxpayer in the comparison
            const mainTaxpayerCategory = getCategory(cardData);
            if (mainTaxpayerCategory && (mostComplexOrder === null || mainTaxpayerCategory.Order < mostComplexOrder)) {
                mostComplexCategory = mainTaxpayerCategory;
            }

            return mostComplexCategory;
        }

        // If MainTaxpayer, just return main taxpayer category
        return getCategory(cardData);
    }

    handleCardComplexity(cardData) {
        // Categories calculation behavior according to values:
        //
        // Null -> User selected the 'Unassigned' value - Don't calculate according to tags
        // 'Unassigned' -> User has not selected any value - Calculate category according to tags
        // Any other value -> User selected a value - Don't calculate according to tags

        let categoryResult = null;
        if (cardData.categoryId === "Unassigned") {
            categoryResult = this.categoriesService.calculateCategory(this.currentDashboard, cardData.tags);
            this.calculatedCategoriesPerCard[cardData.cardId] = categoryResult;
        } else if (cardData.categoryId) {
            categoryResult = this.categoriesService.getCategory(cardData.categoryId);
            if (this.calculatedCategoriesPerCard[cardData.cardId]) {
                if (categoryResult && this.calculatedCategoriesPerCard[cardData.cardId].Id === categoryResult.Id) {
                    // Same as calculated, remove it from card
                    cardData.categoryId = "Unassigned";
                } else {
                    // Manually overridden
                    delete this.calculatedCategoriesPerCard[cardData.cardId];
                }
            }
        }
        return categoryResult;
    }
    // Card field calc section
    private calcCardFieldLabelsCache = {};

    clearCardCacheById(cardId: string): void {
        if (!this.calcCardFieldLabelsCache || typeof this.calcCardFieldLabelsCache !== "object") {
            // Cache is not initialized or not an object
            return;
        }
        if (this.calcCardFieldLabelsCache?.[cardId]) {
            delete this.calcCardFieldLabelsCache[cardId];
        }
    }

    calcCardFieldLabels(cardData, statusIds, skipCache = false): ICardFieldLabelsModel {
        if (!skipCache && this.calcCardFieldLabelsCache[cardData.cardId]?.[cardData._ts + statusIds.join("")]) {
            return this.calcCardFieldLabelsCache[cardData.cardId][cardData._ts + statusIds.join("")];
        }
        const result = {
            deadlineDateFromNow: null,
            deadlineDaysFromNow: null,
            deadlineDate: null,
            documentsReceivedDate: null,
            lastYearDocumentsReceivedDate: null,
            lastYearDocumentsReceivedDateByLanguage: null,
            clientFileCount: null,
            daysSinceInStep: null,
            secondsSinceInStep: null,
            daysSinceReady: null,
            isStepWarning: null,
            isStepAlert: null,
            isDeadlineWarning: null,
            isDeadlineAlert: null,
            quietDaysFromNow: 0,
            isEFileStatusAlert: false,
            documentReceivedLate: false,
            thisYearCompletionDate: null,
            thisYearCompletionDateTimestamp: 0,
            lastYearCompletedDateTimestamp: 0,
            lastYearCompletedDate: null,
            thisYearCompletionDateByLanguage: null,
            lastYearCompletedDateByLanguage: null,
            complexity: null,
        } as ICardFieldLabelsModel;

        const timeDifference = 12 * 60 * 60 + new Date().getTimezoneOffset() * 60;

        if (isJobsBillingDashboard(this.currentDashboard)) {
            if (cardData.job?.targetEndDate) {
                result.deadlineDateFromNow = cardData.job?.targetEndDate
                    ? this.helpersService.dateFromUnix(cardData.job?.targetEndDate + timeDifference)
                    : null;
                result.deadlineDaysFromNow = cardData.job?.targetEndDate
                    ? this.helpersService.daysDifferenceUnixWithSkipTime(cardData.job?.targetEndDate + timeDifference)
                    : Number.POSITIVE_INFINITY;
                result.deadlineDate = cardData.job?.targetEndDate
                    ? this.helpersService.dateUnix(cardData.job?.targetEndDate + timeDifference, "MMMM d, yyyy")
                    : null;
            } else {
                result.deadlineDateFromNow = cardData.deadLine ? this.helpersService.dateFromUnix(cardData.deadLine + timeDifference) : null;
                result.deadlineDaysFromNow = cardData.deadLine
                    ? this.helpersService.daysDifferenceUnixWithSkipTime(cardData.deadLine + timeDifference)
                    : Number.POSITIVE_INFINITY;
                result.deadlineDate = cardData.deadLine ? this.helpersService.dateUnix(cardData.deadLine + timeDifference, "MMMM d, yyyy") : null;
            }
        } else {
            if (cardData.dateNeeded) {
                result.deadlineDateFromNow = cardData.dateNeeded ? this.helpersService.dateFromUnix(cardData.dateNeeded + timeDifference) : null;
                result.deadlineDaysFromNow = cardData.dateNeeded
                    ? this.helpersService.daysDifferenceUnixWithSkipTime(cardData.dateNeeded + timeDifference)
                    : Number.POSITIVE_INFINITY;
                result.deadlineDate = cardData.dateNeeded ? this.helpersService.dateUnix(cardData.dateNeeded + timeDifference, "MMMM d, yyyy") : null;
            } else {
                result.deadlineDateFromNow = cardData.deadLine ? this.helpersService.dateFromUnix(cardData.deadLine + timeDifference) : null;
                result.deadlineDaysFromNow = cardData.deadLine
                    ? this.helpersService.daysDifferenceUnixWithSkipTime(cardData.deadLine + timeDifference)
                    : Number.POSITIVE_INFINITY;
                result.deadlineDate = cardData.deadLine ? this.helpersService.dateUnix(cardData.deadLine + timeDifference, "MMMM d, yyyy") : null;
            }
        }

        if (cardData.documentsReceivedDate) {
            result.documentsReceivedDate = this.helpersService.dateUnix(cardData.documentsReceivedDate, "MMMM d, yyyy");
        }
        if (cardData.lastYearDocumentsReceivedDate) {
            result.lastYearDocumentsReceivedDate = this.helpersService.dateUnix(cardData.lastYearDocumentsReceivedDate, "MMMM d, yyyy");
            result.lastYearDocumentsReceivedDateByLanguage = this.helpersService.dateUnix(
                cardData.lastYearDocumentsReceivedDate,
                this.helpersService.getLang() === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy",
            );
        }
        result.clientFileCount = this.helpersService.getClientFileCount(cardData);
        if (cardData.quiet) {
            if (this.helpersService.convertMillisecondsToSeconds(Date.now()) < cardData.quiet) {
                result.quietDaysFromNow = this.helpersService.daysDifferenceFromFutureUnix(cardData.quiet, true);
            }
        }

        if (cardData.lastYearDocumentsReceivedDate) {
            result.documentReceivedLate =
                cardData.lastYearDocumentsReceivedDate + HelpersService.secondsInAYear <=
                    this.helpersService.convertMillisecondsToSeconds(Date.now()) && !cardData.documentsReceivedDate;
        }

        result.complexity = this.handleCardComplexity(cardData)?.Name;

        const daysSinceResult = this.getDaysSinceFields(cardData, statusIds);
        result.daysSinceInStep = daysSinceResult.daysSinceInStep;
        result.secondsSinceInStep = daysSinceResult.secondsSinceInStep;
        result.daysSinceReady = daysSinceResult.daysSinceReady;
        result.isStepWarning = this.helpersService.checkCardStepWarning(result, cardData);
        result.isStepAlert = this.helpersService.checkCardStepAlert(result, cardData);
        result.isDeadlineWarning = this.helpersService.checkCardDeadlineWarning(result, cardData);
        result.isDeadlineAlert = this.helpersService.checkCardDeadlineAlert(result, cardData);
        result.isEFileStatusAlert = this.helpersService.checkCardEFileStatusAlert(cardData);
        result.thisYearCompletionDate = daysSinceResult.thisYearCompletionDate;
        result.lastYearCompletedDate = daysSinceResult.lastYearCompletedDate;
        result.thisYearCompletionDateByLanguage = daysSinceResult.thisYearCompletionDateByLanguage;
        result.lastYearCompletedDateByLanguage = daysSinceResult.lastYearCompletedDateByLanguage;
        result.thisYearCompletionDateTimestamp = daysSinceResult.thisYearCompletionDateTimestamp;
        result.lastYearCompletedDateTimestamp = daysSinceResult.lastYearCompletedDateTimestamp;
        this.calcCardFieldLabelsCache[cardData.cardId] ??= {}; //only store the latest ts.
        this.calcCardFieldLabelsCache[cardData.cardId][cardData._ts + statusIds.join("")] = result;
        return result;
    }

    getCardsFromStep(stepID) {
        let resultData = {};
        const stepModel = this.model?.find((m) => m.stepId === stepID);
        if (stepModel) {
            resultData = stepModel.cards.reduce((acc, card) => {
                acc[card.cardId] = card;
                return acc;
            }, {});
        }
        return resultData;
    }

    getCardsByStatusIds(statusIds) {
        let cards = {};
        if (!Array.isArray(statusIds)) {
            statusIds = [statusIds];
        }

        for (const statusId of statusIds) {
            const tmpCards = this.model.find((m) => m.statusId === statusId)?.cards || [];
            cards = {
                ...cards,
                ...tmpCards.reduce((acc, card) => {
                    acc[card.cardId] = card;
                    return acc;
                }, {}),
            };
        }
        return cards;
    }

    getDaysSinceFields(cardData, statusIds) {
        let stepStatusDates = [];
        let readyStatusDates = [];
        const result = {
            daysSinceInStep: 0,
            secondsSinceInStep: 0,
            daysSinceReady: 0,
            thisYearCompletionDate: null,
            thisYearCompletionDateTimestamp: 0,
            lastYearCompletedDate: null,
            lastYearCompletedDateTimestamp: 0,
            thisYearCompletionDateByLanguage: null,
            lastYearCompletedDateByLanguage: null,
        };
        let lastCompletedStatus = 0;
        const modelStatusIds = this.model.reduce(
            (acc: Record<string, string>, m: CardDisplayModel) => {
                acc[m.statusId] = m.state;
                return acc;
            },
            {} as Record<string, string>,
        );

        let history: IReturnStatusHistory[] = [];

        if (
            this.currentDashboard.DisplayGroupedCards &&
            this.currentDashboard.DisplayCardBasedOn === DisplayCardBasedOn.LeastAdvanced &&
            !cardData.isSplit &&
            cardData.members?.length > 0
        ) {
            const membersHistory = [
                {
                    history: cardData.statusesHistory.sort(this.helpersService.sortStatusHistoryDescending),
                    historyIdx: 0,
                },
            ];

            cardData.members.forEach((member) => {
                const memberCard = this.getCardById(member.cardId);
                if (memberCard) {
                    membersHistory.push({
                        history: memberCard.statusesHistory.sort(this.helpersService.sortStatusHistoryDescending),
                        historyIdx: 0,
                    });
                }
            });

            let historiesRemaining = membersHistory.length;
            while (historiesRemaining > 0) {
                const historiesToProcess: IReturnStatusHistory[] = [];

                if (membersHistory[0].historyIdx < membersHistory[0].history.length) {
                    const history = membersHistory[0].history[membersHistory[0].historyIdx];
                    historiesToProcess.push(history);
                }
                for (const element of membersHistory.slice(1)) {
                    if (element.historyIdx < element.history.length) {
                        const history = element.history[element.historyIdx];
                        historiesToProcess.push(history);
                    }
                }

                let leastAdvancedHistory = historiesToProcess.reduce((leastAdvancedHistory, statusHistory) => {
                    const leastAdvancedStep = this.mappingOfStatusesService.getStepFromStatusId(leastAdvancedHistory.statusId);
                    const historyStep = this.mappingOfStatusesService.getStepFromStatusId(statusHistory.statusId);
                    if (historyStep && leastAdvancedStep && StepsService.compareSteps(historyStep, leastAdvancedStep) < 0) {
                        leastAdvancedHistory = statusHistory;
                    }
                    return leastAdvancedHistory;
                }, historiesToProcess[0]);

                historiesToProcess.forEach((history) => {
                    if (history.statusId === leastAdvancedHistory.statusId && history.statusDate < leastAdvancedHistory.statusDate) {
                        leastAdvancedHistory = history;
                    }
                });

                for (const element of membersHistory) {
                    while (
                        element.historyIdx < element.history.length &&
                        element.history[element.historyIdx].statusDate >= leastAdvancedHistory.statusDate
                    ) {
                        element.historyIdx++;
                        if (element.historyIdx >= element.history.length) {
                            historiesRemaining--;
                        }
                    }
                }
                history.push(leastAdvancedHistory);
            }
        } else {
            history = cardData.statusesHistory;
        }

        if (isJobsBillingDashboard(this.currentDashboard)) {
            history.sort((a, b) => {
                const dateComparison = this.helpersService.sortStatusHistoryAscending(a, b);
                if (dateComparison !== 0) {
                    return dateComparison;
                }

                // Dates are identical, when J&B are filling the gap between statuses, we need to sort by the order of the status
                if (a.statusId && b.statusId) {
                    // Some statuses are empty in the history
                    const aOrder = this.jobsAndBillingStatusOrderById.get(a.statusId) ?? Number.POSITIVE_INFINITY;
                    const bOrder = this.jobsAndBillingStatusOrderById.get(b.statusId) ?? Number.POSITIVE_INFINITY;
                    if (aOrder !== bOrder) {
                        return aOrder - bOrder;
                    }
                }
                return 0;
            });
        } else {
            history.sort(this.helpersService.sortStatusHistoryAscending);
        }
        history.forEach((statusHistory) => {
            const step = modelStatusIds[statusHistory.statusId];
            if (step) {
                if (statusIds.includes(statusHistory.statusId)) {
                    stepStatusDates.push(statusHistory.statusDate);
                } else {
                    stepStatusDates = [];
                }
                if (step === "active") {
                    readyStatusDates.push(statusHistory.statusDate);
                    if (lastCompletedStatus) {
                        lastCompletedStatus = 0;
                    }
                } else if (step === "completed") {
                    if (!lastCompletedStatus) {
                        lastCompletedStatus = statusHistory.statusDate;
                    }
                } else if (step === "notStarted") {
                    // Not started or other state encountered, reset other variables to restart counters
                    readyStatusDates = [];
                    lastCompletedStatus = 0;
                }
            }
        });

        if (cardData.lastYearCompletedDate) {
            result.lastYearCompletedDate = this.helpersService.dateUnix(cardData.lastYearCompletedDate, "MMMM d, yyyy");
            result.lastYearCompletedDateTimestamp = cardData.lastYearCompletedDate;
            result.lastYearCompletedDateByLanguage = this.helpersService.dateUnix(
                cardData.lastYearCompletedDate,
                this.helpersService.getLang() === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy",
            );
        }

        if (stepStatusDates.length > 0) {
            const lowestStepStatusDate = stepStatusDates.reduce((a, b) => Math.min(a, b));
            result.daysSinceInStep = this.helpersService.daysDifferenceUnix(lowestStepStatusDate);
            result.secondsSinceInStep = this.helpersService.secondsDifferenceUnix(lowestStepStatusDate);
        }
        if (lastCompletedStatus) {
            result.thisYearCompletionDateTimestamp = lastCompletedStatus;
            result.thisYearCompletionDate = this.helpersService.dateUnix(lastCompletedStatus, "MMMM d, yyyy");
            result.thisYearCompletionDateByLanguage = this.helpersService.dateUnix(
                lastCompletedStatus,
                this.helpersService.getLang() === "en-CA" ? "MMMM d, yyyy" : "d MMMM yyyy",
            );
        }
        if (readyStatusDates.length > 0) {
            const lowestReadyStatusDate = readyStatusDates.reduce((a, b) => Math.min(a, b));
            if (lastCompletedStatus) {
                result.daysSinceReady = this.helpersService.daysDifferenceUnix(lowestReadyStatusDate, false, lastCompletedStatus);
            } else {
                result.daysSinceReady = this.helpersService.daysDifferenceUnix(lowestReadyStatusDate);
            }
        }
        return result;
    }
}
